import { useCallback, useEffect, useState } from 'react';
import ComponentRenderer from '../components/ComponentRenderer';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { visibilityOptions } from '../data/widgets';
import {
  installComponentTemplate,
  listComponentTemplates,
  listUserComponents,
  updateUserComponent,
} from '../services/componentService';
import {
  createComponentRun,
  dailyStatsForComponent,
  listComponentRuns,
  summarizeRuns,
  todayRunDate,
} from '../services/componentRunService';

const emptyRunForm = {
  componentId: '',
  runDate: todayRunDate(),
  status: 'completed',
  responseText: '',
  visibility: 'private',
};

const emptyCustomDaily = {
  title: '',
  description: '',
  prompt: '',
  category: 'custom',
  visibility: 'private',
};

function Daily() {
  const { currentUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [components, setComponents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [runForm, setRunForm] = useState(emptyRunForm);
  const [customDaily, setCustomDaily] = useState(emptyCustomDaily);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [statsComponentId, setStatsComponentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadDaily = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [loadedTemplates, loadedComponents, loadedRuns] = await Promise.all([
        listComponentTemplates(),
        listUserComponents(currentUser.uid),
        listComponentRuns(currentUser.uid),
      ]);
      const dailyComponents = loadedComponents.filter((component) => component.kind === 'task' || component.kind === 'game');
      setTemplates(loadedTemplates.filter((template) => template.kind === 'task' || template.kind === 'game'));
      setComponents(dailyComponents);
      setRuns(loadedRuns);
      setRunForm((current) => ({
        ...current,
        componentId: current.componentId || dailyComponents[0]?.id || '',
      }));
      setStatsComponentId((current) => current || dailyComponents[0]?.id || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadDaily();
  }, [loadDaily]);

  const selectedComponent = components.find((component) => component.id === runForm.componentId) || null;
  const statsComponent = components.find((component) => component.id === statsComponentId) || null;
  const summary = summarizeRuns(runs);
  const selectedStats = statsComponent ? dailyStatsForComponent(runs, statsComponent.id) : null;

  function updateRunForm(field, value) {
    setRunForm((current) => ({ ...current, [field]: value }));
  }

  function updateCustomDaily(field, value) {
    setCustomDaily((current) => ({ ...current, [field]: value }));
  }

  async function handleInstall(template) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await installComponentTemplate(currentUser.uid, template, components.length);
      setMessage(`${template.name} added to your daily workspace.`);
      setShowTemplatePicker(false);
      await loadDaily();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateCustom(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (!customDaily.title.trim()) {
        throw new Error('Daily title is required.');
      }
      const template = {
        id: `custom-daily-${Date.now()}`,
        name: customDaily.title.trim(),
        description: customDaily.description.trim() || 'A custom daily practice.',
        kind: 'task',
        category: customDaily.category,
        defaultVisibility: customDaily.visibility,
        placement: 'dashboard',
        defaultSettings: {
          prompt: customDaily.prompt.trim() || 'What do you want to log?',
        },
      };
      await installComponentTemplate(currentUser.uid, template, components.length);
      setCustomDaily(emptyCustomDaily);
      setShowCustomForm(false);
      setMessage('Custom daily created.');
      await loadDaily();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (!selectedComponent) {
        throw new Error('Choose a daily task or game first.');
      }
      await createComponentRun(currentUser.uid, selectedComponent, {
        runDate: runForm.runDate,
        status: runForm.status,
        response: { text: runForm.responseText.trim() },
        score: runForm.status === 'completed' ? 1 : 0,
        visibility: runForm.visibility,
      });
      setRunForm((current) => ({ ...emptyRunForm, componentId: current.componentId, runDate: todayRunDate() }));
      setMessage('Daily run saved.');
      await loadDaily();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleShowOnProfile(component) {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const dailyStatsTemplate = {
        id: 'daily-stats',
        name: `${component.title} Stats`,
        description: 'Shows selected daily history on your profile.',
        kind: 'data-view',
        category: 'daily',
        defaultVisibility: component.visibility || 'private',
        placement: 'profile',
        dataSource: 'component-runs',
        defaultSettings: {
          title: `${component.title} Stats`,
          sourceComponentId: component.id,
          sourceTemplateId: component.templateId,
          showStreak: true,
        },
      };
      const instanceId = await installComponentTemplate(currentUser.uid, dailyStatsTemplate, components.length);
      await updateUserComponent(currentUser.uid, instanceId, {
        ...dailyStatsTemplate,
        title: dailyStatsTemplate.name,
        templateId: 'daily-stats',
        settings: dailyStatsTemplate.defaultSettings,
        visibility: dailyStatsTemplate.defaultVisibility,
        position: components.length,
        placement: 'profile',
        dataSource: 'component-runs',
      });
      setMessage(`${component.title} can now appear on your profile through a Daily Stats component.`);
      await loadDaily();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <SEO title="Daily" />
      <section className="page-heading daily-hero">
        <p className="eyebrow">Daily life practice</p>
        <h1>Log the small things that become your biography.</h1>
        <p>
          Daily is where reflections, habits, games, fitness, creative work, and regular routines become history.
          Each daily can stay private, be shared with friends, or feed selected stats into your profile.
        </p>
        <div className="button-row">
          <button className="button" type="button" onClick={() => setShowTemplatePicker((current) => !current)}>Add daily</button>
          <button className="button button-secondary" type="button" onClick={() => setShowCustomForm((current) => !current)}>Create your own daily</button>
        </div>
      </section>
      {error && <p className="error-text">{error}</p>}
      {message && <p className="notice">{message}</p>}
      <section className="feature-grid compact-stats-grid">
        <article className="surface-card">
          <p className="tag">Runs</p>
          <h2>{summary.total}</h2>
          <p>Total daily entries.</p>
        </article>
        <article className="surface-card">
          <p className="tag">Completed</p>
          <h2>{summary.completed}</h2>
          <p>Completed tasks and games.</p>
        </article>
        <article className="surface-card">
          <p className="tag">Last completed</p>
          <h2>{summary.lastCompleted || 'None yet'}</h2>
          <p>Your latest completed daily entry.</p>
        </article>
      </section>
      {showTemplatePicker ? (
        <section className="surface-card">
          <div className="section-toolbar">
            <div>
              <h2>Choose a daily</h2>
              <p className="muted">Start with a default, then customize it in Components later.</p>
            </div>
          </div>
          <div className="feature-grid daily-template-grid">
            {templates.map((template) => (
              <article className="content-item" key={template.id}>
                <p className="tag">{template.category}</p>
                <h3>{template.name}</h3>
                <p>{template.description}</p>
                <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleInstall(template)}>Add this daily</button>
              </article>
            ))}
          </div>
        </section>
      ) : null}
      {showCustomForm ? (
        <section className="surface-card">
          <form className="stacked-form" onSubmit={handleCreateCustom}>
            <h2>Create your own daily</h2>
            <label>Title<input value={customDaily.title} onChange={(event) => updateCustomDaily('title', event.target.value)} placeholder="Reading log, ranked games, skincare, language practice" /></label>
            <label>Description<textarea rows="3" value={customDaily.description} onChange={(event) => updateCustomDaily('description', event.target.value)} /></label>
            <label>Prompt<textarea rows="3" value={customDaily.prompt} onChange={(event) => updateCustomDaily('prompt', event.target.value)} placeholder="What should this daily ask you?" /></label>
            <label>Category
              <select value={customDaily.category} onChange={(event) => updateCustomDaily('category', event.target.value)}>
                <option value="custom">Custom</option>
                <option value="fitness">Fitness</option>
                <option value="gaming">Gaming</option>
                <option value="creative">Creative</option>
                <option value="writing">Writing</option>
                <option value="wellness">Wellness</option>
              </select>
            </label>
            <label>Default privacy
              <select value={customDaily.visibility} onChange={(event) => updateCustomDaily('visibility', event.target.value)}>
                {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
            </label>
            <button className="button" type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create daily'}</button>
          </form>
        </section>
      ) : null}
      <section className="editor-layout">
        <div className="surface-card">
          <h2>Your dailies</h2>
          {loading ? <p>Loading daily workspace...</p> : null}
          {!loading && components.length === 0 ? <p className="muted">No dailies yet. Use Add daily to start with a default or create your own.</p> : null}
          <div className="item-list">
            {components.map((component) => (
              <article className="content-item daily-card" key={component.id}>
                <p className="tag">{component.kind} · {component.visibility}</p>
                <h3>{component.title}</h3>
                <ComponentRenderer component={component} />
                <div className="button-row compact-row">
                  <button className="button button-secondary" type="button" onClick={() => updateRunForm('componentId', component.id)}>Log today</button>
                  <button className="button button-secondary" type="button" onClick={() => setStatsComponentId(component.id)}>Statistics</button>
                  <button className="button button-secondary" type="button" disabled={saving} onClick={() => handleShowOnProfile(component)}>Show on profile</button>
                </div>
              </article>
            ))}
          </div>
        </div>
        <form className="surface-card stacked-form" onSubmit={handleSubmit}>
          <h2>Log today</h2>
          <label>Daily
            <select value={runForm.componentId} onChange={(event) => updateRunForm('componentId', event.target.value)}>
              <option value="">Choose one</option>
              {components.map((component) => <option value={component.id} key={component.id}>{component.title}</option>)}
            </select>
          </label>
          {selectedComponent ? <ComponentRenderer component={selectedComponent} /> : <p className="muted">Add a daily to start tracking.</p>}
          <label>Date<input type="date" value={runForm.runDate} onChange={(event) => updateRunForm('runDate', event.target.value)} /></label>
          <label>Status
            <select value={runForm.status} onChange={(event) => updateRunForm('status', event.target.value)}>
              <option value="completed">Completed</option>
              <option value="skipped">Skipped</option>
            </select>
          </label>
          <label>Response<textarea rows="5" value={runForm.responseText} onChange={(event) => updateRunForm('responseText', event.target.value)} placeholder="Write a reflection, stat, score, answer, or result." /></label>
          <label>Visibility
            <select value={runForm.visibility} onChange={(event) => updateRunForm('visibility', event.target.value)}>
              {visibilityOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
            </select>
          </label>
          <button className="button" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save daily run'}</button>
        </form>
      </section>
      <section className="editor-layout">
        <div className="surface-card">
          <h2>Statistics</h2>
          {!statsComponent || !selectedStats ? <p className="muted">Choose a daily and click Statistics.</p> : (
            <>
              <p className="tag">{statsComponent.title}</p>
              <div className="stats-list">
                <span>Day: {selectedStats.day}</span>
                <span>Week: {selectedStats.week}</span>
                <span>Month: {selectedStats.month}</span>
                <span>Year: {selectedStats.year}</span>
                <span>All time: {selectedStats.allTime}</span>
                <span>Completed: {selectedStats.completed}</span>
                <span>Skipped: {selectedStats.skipped}</span>
              </div>
            </>
          )}
        </div>
        <div className="surface-card">
          <h2>Recent history</h2>
          {!loading && runs.length === 0 ? <p className="muted">No daily history yet.</p> : null}
          <div className="item-list">
            {runs.slice(0, 6).map((run) => (
              <article className="content-item" key={run.id}>
                <p className="tag">{run.runDate} · {run.status} · {run.visibility}</p>
                <h3>{run.templateId}</h3>
                <p>{run.response?.text || 'No response text.'}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Daily;
